import React, { useEffect, useState } from 'react';
import { groupItemsByKey, isArrayLength } from '../../util/genericHelpers';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { languagesHelper as language } from '../../helpers/languages';
import { listingFields } from '../../config/configListing';
import css from './OrderPanel.module.css';

const getSizeOrder = () => {
  const sizeField = listingFields.find(field => field.key === 'size');
  return sizeField ? sizeField.enumOptions.map(option => option.label) : [];
};

const VariantWrapper = ({
  intl,
  listingId,
  variantListings,
  formApi,
  selectedVariant,
  setSelectedVariant,
}) => {
  if (!isArrayLength(variantListings)) {
    return null;
  }

  const [showIconInfo, setShowIconInfo] = useState(false);

  // Find and place the listing with the matching UUID first
  const baseListingIndex = variantListings.findIndex(l => l.id.uuid === listingId?.uuid);
  const baseListing = variantListings[baseListingIndex];
  const reorderedListings = baseListing
    ? [baseListing, ...variantListings.filter((_, index) => index !== baseListingIndex)]
    : variantListings;

  const groupedBySize = groupItemsByKey(reorderedListings, 'size');

  const sizeOrder = getSizeOrder();

  const sortedGroupedBySize = Object.entries(groupedBySize).sort(([a], [b]) => {
    const indexA = sizeOrder.indexOf(a);
    const indexB = sizeOrder.indexOf(b);
    return indexA - indexB;
  });

  useEffect(() => {
    // Attempt to set the selected variant to the base listing if available
    if (baseListing && baseListing.stock === 1) {
      formApi.change('variantListingId', baseListing.id.uuid);
      setSelectedVariant(baseListing);
    } else {
      // Otherwise, find the first available variant from the reordered listings
      for (let size in groupedBySize) {
        const firstAvailable = groupedBySize[size].find(variant => variant.stock === 1);
        if (firstAvailable) {
          formApi.change('variantListingId', firstAvailable.id.uuid);
          setSelectedVariant(firstAvailable);
          break;
        }
      }
    }
  }, []);

  return (
    <div className={css.variantWrapper}>
      <div className={css.variantBlock}>
        <label htmlFor="size" className={css.sizeLabel}>
          {intl.formatMessage({ id: 'VariantWrapper.sizeLabel' })}
        </label>{' '}
        <div className={css.mainVariantWrapper}>
          {sortedGroupedBySize.map(([size, listings]) => (
            <span
              id="size"
              key={size}
              className={classNames(css.mainVariant, {
                [css.selectedVariant]: selectedVariant?.size === size,
              })}
              tabIndex={0}
              role="button"
              onClick={() => {
                const firstAvailable = listings.find(l => l.stock === 1);
                if (firstAvailable) {
                  formApi.change('variantListingId', firstAvailable.id.uuid);
                  setSelectedVariant(firstAvailable);
                }
              }}
            >
              {(() => {
                const sizeKey =
                  size?.indexOf('/') !== -1 ? size?.slice(0, size?.indexOf('/')).trim() : size;
                const translatedSize = language.labelsTranslator(sizeKey, intl);
                return translatedSize?.startsWith('CustomLabel.') ? sizeKey : translatedSize;
              })()}
            </span>
          ))}
        </div>
        <div className={css.conditionLabelWrapper}>
          <label htmlFor="condition" className={css.conditionLabel}>
            {intl.formatMessage({ id: 'VariantWrapper.conditionLabel' })}
          </label>{' '}
          <div
            className={css.infoIcon}
            onMouseEnter={() => setShowIconInfo(true)}
            onMouseLeave={() => setShowIconInfo(false)}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            {showIconInfo && (
              <ul className={css.iconInfoBox}>
                <li>
                  <strong>
                    {intl.formatMessage({ id: 'ConditionLabel.newWithTagsMarshall' })}
                  </strong>
                  {intl.formatMessage({ id: 'ConditionLabel.newWithTagsDetailsMarshall' })}
                </li>
                <li>
                  <strong>{intl.formatMessage({ id: 'ConditionLabel.likeNew' })}</strong>
                  {intl.formatMessage({ id: 'ConditionLabel.likeNewDetails' })}
                </li>
                <li>
                  <strong>{intl.formatMessage({ id: 'ConditionLabel.great' })}</strong>
                  {intl.formatMessage({ id: 'ConditionLabel.greatDetails' })}
                </li>
                <li>
                  <strong>{intl.formatMessage({ id: 'ConditionLabel.good' })}</strong>
                  {intl.formatMessage({ id: 'ConditionLabel.goodDetailsMarshall' })}
                </li>
              </ul>
            )}
          </div>
        </div>
        {Object.entries(groupedBySize)
          .filter(([size]) => selectedVariant?.size === size)
          .map(([size, listings]) => (
            <div className={css.subVariant}>
              {listings.map(l => (
                <span
                  id="condition"
                  className={classNames(css.variant, {
                    [css.selectedVariant]: selectedVariant?.id?.uuid === l.id.uuid,
                    [css.disabledVariant]: l.stock !== 1,
                  })}
                  key={l.id.uuid}
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    if (l.stock === 1) {
                      formApi.change('variantListingId', l.id.uuid);
                      setSelectedVariant(l);
                    }
                  }}
                >
                  {/* {l.condition.value} */}
                  {language.labelsTranslator(l.condition.value, intl)}
                </span>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

export default VariantWrapper;
